import { DataProfile } from '@hid/snapshot/profile'
import {
  type KK,
  type SetMagnetSenior,
  Record,
  DataTravel,
  VirtualHE3SEWireless_24g,
  VirtualNa87ProWired,
  VirtualMu68Pro,
  VirtualHE3SEWireless,
  DefineDevice,
  VirtualNd63,
  VirtualNd63_Cyan,
  VirtualMu68Pro_Cyan,
  VirtualMu68_ULTRA,
  VirtualPolar75,
  RoYuanDevice,
  JingTaiMg75Devices,
  JingTaiMer68Devices,
  VirtualHe6Ultra,
  VirtualHe6Ultra_24g,
  VirtualMG68ACE,
  VirtualMG68ACE_W,
  VirtualMG68ACE_W_24g,
  Fe87v2Devices,
  VirtualMer68SESelfDev,
  VirtualRA68,
  VirtualIrokFe87V2IMC,
  VirtualIrokFe87V2IMC_24g,
  HuoChaiRenDevices,
  ZhiHuiWuXian_Device,
  VirtualMG68ACE_UpPlus,
} from '../define/define'
import { deviceMap } from './define_list'
import firmware from '@/upgrade/firmware'
import { allKeysForDevice } from './define_fn'
import { devicePro } from '@/profiles/light_limit'
import { DataDeadZone, DataProfileForDecorate } from '@libs/types/response'
import { SpecificItem } from '@libs/types/enum'
import { JingTaiV2DeviceList, RoYuanND63Devices, RoYuanND68Devices } from '@/define/device_vendor'
import { ra68SideLight } from '@hid/define/define_side_light'

export const createDecorates = (mode: number, speed: number, light: number, color: number, colorsCustom: Map<KK, number>, num: number = 1) => {
  const list: DataProfileForDecorate[] = []
  for (let i = 0; i < num; i++) {
    list.push({
      LightLastIsCustom: false,
      LightMode: {
        Mode: mode,
        Speed: speed,
        Light: light,
        FullColor: true,
        Color: color,
        ColorBG: 0,
        Direction: 0,
        Colors: {
          Data: [0xff0000, 0x00ff00, 0xffff00, 0x0000ff, 0xff00ff, 0x00ffff, 0xffffff],
          Index: 0,
        },
        SideMode: 0,
      },
      ColorsCustom: colorsCustom,
      ColorsDynamic: {
        Mode: 0,
        FullColor: false,
        Color: 0,
        Run: false,
        IsEEvision: false,
      },
    })
  }
  return list
}

const dpiColors = [
  [242, 52, 52],
  [252, 168, 23],
  [247, 229, 0],
  [91, 225, 13],
  [15, 215, 246],
  [19, 118, 248],
  [194, 51, 255],
  [255, 112, 159],
  [103, 42, 246],
]

const defaultHe3se: DataProfile = {
  Keys: [],
  Macros: new Map<number, Record[]>(),
  ColorsCustom: new Map<KK, number>(),
  Mouse: {
    DpiColors: dpiColors,
    Rate: 500,
    DpiIndex: 1,
    DpiList: [400, 800, 1600, 2400, 3200, 6400],
  },
  LightMode: {
    Mode: 1,
    Speed: 4,
    Light: 4,
    FullColor: true,
    Color: 0,
    ColorBG: 0,
    Direction: 0,
    SideMode: 0,
  },
  MagnetData: new Map<number, SetMagnetSenior>(),
  MagnetTravel: new Map<KK, DataTravel>(),
  KeysGroup: [],
  LightLastIsCustom: false,
  ColorsDynamic: {
    Mode: 0,
    FullColor: false,
    Color: 0,
    Run: false,
    IsEEvision: false,
  },
  DeadZone: 0,
  DeadZone1: [0, 0],
  DeadZoneList: new Map<KK, DataDeadZone>(),
  Items: new Map<SpecificItem, number>(),
}
const defaultHe6Ultra: DataProfile = {
  Keys: [],
  Macros: new Map<number, Record[]>(),
  ColorsCustom: new Map<KK, number>(),
  Mouse: {
    DpiColors: [
      [255, 0, 0],
      [0, 0, 255],
      [0, 255, 0],
      [255, 255, 0],
      [0, 255, 255],
      [255, 0, 255],
    ],
    Rate: 1000,
    DpiIndex: 2,
    DpiList: [400, 800, 1600, 3200, 6400, 32000],
  },
  LightMode: {
    Mode: 0,
    Speed: 3,
    Light: 5,
    FullColor: false,
    Color: 0,
    ColorBG: 0,
    Direction: 0,
    SideMode: 0,
  },
  MagnetData: new Map<number, SetMagnetSenior>(),
  MagnetTravel: new Map<KK, DataTravel>(),
  KeysGroup: [],
  LightLastIsCustom: false,
  ColorsDynamic: {
    Mode: 0,
    FullColor: false,
    Color: 0,
    Run: false,
    IsEEvision: false,
  },
  DeadZone: 0,
  DeadZone1: [0, 0],
  DeadZoneList: new Map<KK, DataDeadZone>(),
  Items: new Map<SpecificItem, number>(),
}
const defaultMu68pro: DataProfile = {
  Keys: [],
  Macros: new Map<number, Record[]>(),
  ColorsCustom: new Map<KK, number>(),
  Mouse: {
    DpiColors: [],
    Rate: 0,
    DpiIndex: 0,
    DpiList: [],
  },
  LightMode: {
    Mode: 1,
    Speed: 3,
    Light: 3,
    FullColor: true,
    Color: 0,
    ColorBG: 0,
    Direction: 0,
    SideMode: 0,
  },
  MagnetData: new Map<number, SetMagnetSenior>(),
  MagnetTravel: new Map<KK, DataTravel>(),
  KeysGroup: [],
  LightLastIsCustom: false,
  ColorsDynamic: {
    Mode: 0,
    FullColor: false,
    Color: 0,
    Run: false,
    IsEEvision: false,
  },
  DeadZone: 200,
  DeadZone1: [200, 200],
  DeadZoneList: new Map<KK, DataDeadZone>(),
  Items: new Map<SpecificItem, number>(),
}

const defaultFE87V2: DataProfile = {
  Keys: [],
  Macros: new Map<number, Record[]>(),
  ColorsCustom: new Map<KK, number>(),
  Mouse: {
    DpiColors: [],
    Rate: 0,
    DpiIndex: 0,
    DpiList: [],
  },
  LightMode: {
    Mode: 6,
    Speed: 3,
    Light: 3,
    FullColor: true,
    Color: 0x0000ff, // 默认蓝色 0x0000ff
    ColorBG: 0,
    Direction: 0,
    SideMode: 0,
  },
  MagnetData: new Map<number, SetMagnetSenior>(),
  MagnetTravel: new Map<KK, DataTravel>(),
  KeysGroup: [],
  LightLastIsCustom: false,
  ColorsDynamic: {
    Mode: 0,
    FullColor: false,
    Color: 0,
    Run: false,
    IsEEvision: false,
  },
  DeadZone: 200,
  DeadZone1: [200, 200],
  DeadZoneList: new Map<KK, DataDeadZone>(),
  Items: new Map<SpecificItem, number>(),
  Decorates: createDecorates(0, 3, 3, 0x0000ff, new Map<KK, number>()),
}

const defaultFE87_ZHWX: DataProfile = {
  Keys: [],
  Macros: new Map<number, Record[]>(),
  ColorsCustom: new Map<KK, number>(),
  Mouse: {
    DpiColors: [],
    Rate: 0,
    DpiIndex: 0,
    DpiList: [],
  },
  LightMode: {
    Mode: 0,
    Speed: 1,
    Light: 3,
    FullColor: false,
    Color: 0xffffff, // 默认白色 0xFFFFFF
    ColorBG: 0,
    Direction: 0,
    SideMode: 0,
  },
  MagnetData: new Map<number, SetMagnetSenior>(),
  MagnetTravel: new Map<KK, DataTravel>(),
  KeysGroup: [],
  LightLastIsCustom: false,
  ColorsDynamic: {
    Mode: 0,
    FullColor: false,
    Color: 0,
    Run: false,
    IsEEvision: false,
  },
  DeadZone: 200,
  DeadZone1: [200, 200],
  DeadZoneList: new Map<KK, DataDeadZone>(),
  Items: new Map<SpecificItem, number>(),
}
const defaultJingtaiV2: DataProfile = {
  Keys: [],
  Macros: new Map<number, Record[]>(),
  ColorsCustom: new Map<KK, number>(),
  Mouse: {
    DpiColors: [],
    Rate: 0,
    DpiIndex: 0,
    DpiList: [],
  },
  LightMode: {
    Mode: 1,
    Speed: 80,
    Light: 80,
    FullColor: true,
    Color: 0,
    ColorBG: 0,
    Direction: 0,
    Colors: {
      Data: [0xff0000, 0x00ff00, 0xffff00, 0x0000ff, 0xff00ff, 0x00ffff, 0xffffff],
      Index: 0,
    },
    SideMode: 0,
  },
  MagnetData: new Map<number, SetMagnetSenior>(),
  MagnetTravel: new Map<KK, DataTravel>(),
  KeysGroup: [],
  LightLastIsCustom: false,
  ColorsDynamic: {
    Mode: 0,
    FullColor: false,
    Color: 0,
    Run: false,
    IsEEvision: false,
  },
  DeadZone: 200,
  DeadZone1: [200, 200],
  DeadZoneList: new Map<KK, DataDeadZone>(),
  Items: new Map<SpecificItem, number>(),
}

const defaultRA68: DataProfile = {
  Keys: [],
  Macros: new Map<number, Record[]>(),
  ColorsCustom: new Map<KK, number>(),
  Mouse: {
    DpiColors: [],
    Rate: 0,
    DpiIndex: 0,
    DpiList: [],
  },
  LightMode: {
    Mode: 1,
    Speed: 75,
    Light: 75,
    FullColor: true,
    Color: 0,
    ColorBG: 0,
    Direction: 0,
    Colors: {
      Data: [0xff0000, 0x00ff00, 0xffff00, 0x0000ff, 0xff00ff, 0x00ffff, 0xffffff],
      Index: 0,
    },
    SideMode: 0,
  },
  MagnetData: new Map<number, SetMagnetSenior>(),
  MagnetTravel: new Map<KK, DataTravel>(),
  KeysGroup: [],
  LightLastIsCustom: false,
  ColorsDynamic: {
    Mode: 0,
    FullColor: false,
    Color: 0,
    Run: false,
    IsEEvision: false,
  },
  DeadZone: 200,
  DeadZone1: [200, 200],
  DeadZoneList: new Map<KK, DataDeadZone>(),
  Items: new Map<SpecificItem, number>(),
  Decorates: createDecorates(0, 75, 75, 0, ra68SideLight),
}

// Mers 68
const defaultNd68: DataProfile = {
  Keys: [],
  Macros: new Map<number, Record[]>(),
  ColorsCustom: new Map<KK, number>(),
  Mouse: {
    DpiColors: [],
    Rate: 0,
    DpiIndex: 0,
    DpiList: [],
  },
  LightMode: {
    Mode: 4,
    Speed: 1,
    Light: 4,
    FullColor: true,
    Color: 0,
    ColorBG: 0,
    Direction: 0,
    SideMode: 0,
  },
  MagnetData: new Map<number, SetMagnetSenior>(),
  MagnetTravel: new Map<KK, DataTravel>(),
  KeysGroup: [],
  LightLastIsCustom: false,
  ColorsDynamic: {
    Mode: 0,
    FullColor: false,
    Color: 0,
    Run: false,
    IsEEvision: false,
  },
  DeadZone: 30,
  DeadZone1: [30, 30], // 顶部死区，底部死区
  DeadZoneList: new Map<KK, DataDeadZone>(), // 按键死区
  Items: new Map<SpecificItem, number>(),
}

const defaultMG63: DataProfile = {
  Keys: [],
  Macros: new Map<number, Record[]>(),
  ColorsCustom: new Map<KK, number>(),
  Mouse: {
    DpiColors: [],
    Rate: 0,
    DpiIndex: 0,
    DpiList: [],
  },
  LightMode: {
    Mode: 1,
    Speed: 80,
    Light: 80,
    FullColor: true,
    Color: 0,
    ColorBG: 0,
    Direction: 0,
    SideMode: 0,
  },
  MagnetData: new Map<number, SetMagnetSenior>(),
  MagnetTravel: new Map<KK, DataTravel>(),
  KeysGroup: [],
  LightLastIsCustom: false,
  ColorsDynamic: {
    Mode: 0,
    FullColor: false,
    Color: 0,
    Run: false,
    IsEEvision: false,
  },
  DeadZone: 200,
  DeadZone1: [200, 200],
  DeadZoneList: new Map<KK, DataDeadZone>(),
  Items: new Map<SpecificItem, number>(),
  Decorates: createDecorates(1, 80, 80, 0, ra68SideLight),
}

const createDefaultProfile = (template: DataProfile): DataProfile => {
  const profile: DataProfile = {
    Keys: [...template.Keys],
    Macros: new Map(template.Macros),
    ColorsCustom: new Map(template.ColorsCustom),
    Mouse: {
      ...template.Mouse,
      DpiColors: [...template.Mouse.DpiColors],
      DpiList: [...template.Mouse.DpiList],
    },
    LightMode: { ...template.LightMode },
    LightModeSettings: {
      [String(template.LightMode.Mode)]: {
        Light: template.LightMode.Light,
        Speed: template.LightMode.Speed,
      },
    },
    MagnetData: new Map(template.MagnetData),
    MagnetTravel: new Map(template.MagnetTravel),
    KeysGroup: [],
    LightLastIsCustom: template.LightLastIsCustom,
    ColorsDynamic: { ...template.ColorsDynamic },
    DeadZone: template.DeadZone,
    DeadZone1: [...template.DeadZone1],
    DeadZoneList: new Map(template.DeadZoneList),
    Items: new Map<SpecificItem, number>(),
  }
  if (template.Decorates) {
    profile.Decorates = template.Decorates
  }
  return profile
}

export const createProfile = (key: DefineDevice): DataProfile => {
  let template: DataProfile

  const he3seDevices = [VirtualHE3SEWireless_24g, VirtualHE3SEWireless]
  const he6UltraDevices = [VirtualHe6Ultra, VirtualHe6Ultra_24g]
  const magnetDevices = [VirtualNa87ProWired, VirtualMu68Pro, VirtualNd63, VirtualNd63_Cyan, VirtualMu68Pro_Cyan, VirtualMu68_ULTRA, VirtualPolar75, ...JingTaiMg75Devices, ...JingTaiMer68Devices]

  if (he3seDevices.includes(key)) {
    template = defaultHe3se
  } else if (he6UltraDevices.includes(key)) {
    template = defaultHe6Ultra
  } else if (magnetDevices.includes(key)) {
    template = defaultMu68pro
    firmware.fwVersionLessThan('1.0.4', devicePro).then((res) => {
      if (res) {
        template.LightMode.Mode = 0
      }
    })
  } else if (JingTaiV2DeviceList.includes(key)) {
    if (VirtualRA68 == key) {
      template = defaultRA68
    } else {
      template = defaultJingtaiV2
    }
  } else if (RoYuanDevice.includes(key)) {
    template = defaultNd68
  } else if (Fe87v2Devices.includes(key)) {
    template = defaultFE87V2
  } else if (VirtualMer68SESelfDev == key) {
    template = defaultMu68pro
  } else if (HuoChaiRenDevices.includes(key)) {
    template = defaultMG63
    // TODO: 取消键盘中自带的默认颜色，暂时写死，后期需要优化
    template.ColorsCustom.set(0x1a as KK, 0x000000)
    template.ColorsCustom.set(0x04 as KK, 0x000000)
    template.ColorsCustom.set(0x07 as KK, 0x000000)
    template.ColorsCustom.set(0x16 as KK, 0x000000)
  } else if (ZhiHuiWuXian_Device.includes(key)) {
    template = defaultFE87_ZHWX
  } else {
    throw new Error(`Unsupported device key: ${key}`)
  }

  const profile = createDefaultProfile(template)
  const len = deviceMap[key].support.Limit.DpiLen
  if (len > 0) {
    if (profile.Mouse.DpiList.length > len) {
      profile.Mouse.DpiList = profile.Mouse.DpiList.slice(0, len)
    }
    if (profile.Mouse.DpiColors.length > len) {
      profile.Mouse.DpiColors = profile.Mouse.DpiColors.slice(0, len)
    }
  }

  profile.MagnetTravel = generateDefaultTravels(key)
  profile.DeadZoneList = generateDefaultDeadZone(key)
  return profile
}

export const generateDefaultDeadZone = (device: DefineDevice) => {
  let keysDeadZone: Map<KK, DataDeadZone> = new Map()
  if (device == VirtualHE3SEWireless_24g || device == VirtualHE3SEWireless) {
    return keysDeadZone
  }
  const keys = allKeysForDevice(device)
  for (const key of keys) {
    if (RoYuanDevice.includes(device)) {
      keysDeadZone.set(key, {
        Top: 30,
        Bottom: 30,
      })
    } else if (HuoChaiRenDevices.includes(device)) {
      keysDeadZone.set(key, {
        Top: 0,
        Bottom: 0,
      })
    } else {
      keysDeadZone.set(key, {
        Top: 200,
        Bottom: 200,
      })
    }
    if ([VirtualMG68ACE, VirtualMG68ACE_UpPlus, VirtualMG68ACE_W, VirtualMG68ACE_W_24g, ...RoYuanND63Devices, ...RoYuanND68Devices].includes(device)) {
      keysDeadZone.set(key, {
        Top: 60,
        Bottom: 60,
      })
    }
  }
  return keysDeadZone
}

const generateDefaultTravels = (device: DefineDevice) => {
  let keysTravel: Map<KK, DataTravel> = new Map()
  if (device == VirtualHE3SEWireless_24g || device == VirtualHE3SEWireless) {
    return keysTravel
  }
  const keys = allKeysForDevice(device)
  let travel = 2000
  if (RoYuanDevice.includes(device)) {
    travel = 200
  }
  if ([VirtualMG68ACE, VirtualMG68ACE_W, VirtualMG68ACE_W_24g, ...RoYuanND63Devices, ...RoYuanND68Devices].includes(device)) {
    travel = 400
  }
  if (VirtualIrokFe87V2IMC == device || VirtualIrokFe87V2IMC_24g == device || HuoChaiRenDevices.includes(device)) {
    travel = 1500
  }
  for (const key of keys) {
    keysTravel.set(key, {
      PressNum: travel,
      ReleaseNum: travel,
      QuickTriggerPressNum: 0,
      QuickTriggerReleaseNum: 0,
    })
  }
  return keysTravel
}
